import { TextValueFormat } from 'editor/src/store/design/types';

/**
 * The character in a mask that stands for one digit the buyer types.
 *
 * Deliberately not a digit. Mask languages commonly use "0" or "9", which
 * works when a developer writes the mask and knows to escape, but breaks the
 * moment the pattern contains a literal digit — and phone masks nearly always
 * do: "+380(67)" is three literal digits followed by two more. An author
 * writing "+380(67)xxx-xx-xx" by hand must get the number they wrote, so the
 * placeholder is taken from outside the value's own alphabet.
 */
export const MASK_SLOT = 'x';

/** Escapes the next mask character, for the rare literal "x" (e.g. "\x123"). */
const MASK_ESCAPE = '\\';

type MaskToken = { slot: true } | { slot: false; char: string };

/**
 * Normalises a personalisation value to the template's canonical form.
 *
 * The formatted result is written back into `extra.text` rather than applied
 * at render time. That is deliberate: the design data is what `saveDesign`
 * posts and what the print file is generated from, so normalising the stored
 * value keeps preview and print identical by construction. A render-time
 * transform would have to be reimplemented in the print path and could drift.
 */
function applyValueFormat(value: string, format: TextValueFormat | undefined): string {
  if (!format) {
    return value;
  }

  switch (format.kind) {
    case 'mask':
      return formatMask(value, format.mask);
    case 'emailDomain':
      return formatEmailDomain(value, format);
    default:
      return value;
  }
}

/**
 * Pours the digits the buyer typed into the mask, dropping whatever
 * separators they used: "+380(67)xxx-xx-xx" turns "671234567" into
 * "+380(67)123-45-67". Digits past the last slot are appended rather than
 * dropped, so a longer international number is not silently truncated.
 */
function formatMask(value: string, mask: string): string {
  const tokens = parseMask(mask);
  if (!tokens.some((token) => token.slot)) {
    // A mask with nowhere to put a digit cannot say anything about the value.
    // This is the normal state while an author is still typing the pattern, so
    // the value has to survive it untouched.
    return value;
  }

  const digits = maskedDigits(value, tokens);
  if (!digits) {
    // Nothing to pour. An empty value stays empty, so an optional reflow row
    // still collapses on a blank field. Anything else is handed back as
    // written rather than wiped: a mask formats input, it does not judge it,
    // and returning "" here destroyed the author's own text the moment they
    // picked a format — which collapsed the row, hid the element and
    // deselected the card out from under them.
    return value;
  }

  let result = '';
  let cursor = 0;
  for (let i = 0; i < tokens.length && cursor < digits.length; i += 1) {
    const token = tokens[i];
    if (token.slot) {
      result += digits[cursor];
      cursor += 1;
    } else {
      result += token.char;
    }
  }

  return result + digits.slice(cursor);
}

/** Splits the mask into digit slots and literal characters, honouring "\". */
function parseMask(mask: string): MaskToken[] {
  const tokens: MaskToken[] = [];
  for (let i = 0; i < mask.length; i += 1) {
    const char = mask[i];
    if (char === MASK_ESCAPE && i + 1 < mask.length) {
      i += 1;
      tokens.push({ slot: false, char: mask[i] });
    } else if (char.toLowerCase() === MASK_SLOT) {
      tokens.push({ slot: true });
    } else {
      tokens.push({ slot: false, char });
    }
  }

  return tokens;
}

/**
 * The digits to pour into the mask.
 *
 * A mask that opens with literals ("+380(67)") would otherwise eat its own
 * output: re-formatting "+380(67)123-45-67" would read that 380 and 67 back as
 * typed input and shift the number along. Stripping the leading literals first
 * keeps the formatter idempotent, which matters because every commit re-runs
 * it.
 */
function maskedDigits(value: string, tokens: MaskToken[]): string {
  const firstSlot = tokens.findIndex((token) => token.slot);
  const prefix = tokens
    .slice(0, firstSlot)
    .map((token) => (token.slot ? '' : token.char))
    .join('');
  const body = prefix && value.startsWith(prefix) ? value.slice(prefix.length) : value;
  return body.replace(/\D/g, '');
}

/**
 * Keeps the local part the buyer typed and forces the template's domain, so
 * "elina@gmail.com" renders as "elina@whitecase.com". An empty local part
 * yields an empty value rather than a bare "@domain" — the line is then blank
 * and an optional reflow row collapses it, which is what the card wants.
 */
function formatEmailDomain(value: string, format: Extract<TextValueFormat, { kind: 'emailDomain' }>): string {
  const localPart = value.split('@')[0].trim();
  if (!localPart) {
    return '';
  }

  const domain = format.domain.replace(/^@/, '');
  return domain ? `${localPart}@${domain}` : localPart;
}

export default applyValueFormat;
